// ── Sessions tab — the cohort schedule (Wave 3, REAL read) ──────────────────
// Reads GET /cohorts/:id/sessions via sessions.listForCohort wrapped in
// useHonestQuery -> CohortTabShell. The cohort-scoped schedule writes live on
// their own pages; this tab is their entry point: "Create ten-week schedule"
// (/cohorts/:id/sessions/series) and "Clear future sessions"
// (/cohorts/:id/sessions/clear). Each shows only when its page would let the
// role act, using the same checks those pages make.

import { Button, CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { buildRolePath } from "../../../components/RouteActionButton";
import { useHonestQuery } from "../../../components/honest-state";
import { evaluateActionCapability } from "../../../domain/capabilities";
import type { CoachingRole } from "../../../domain/roles";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingSessionDto, ListResponse } from "../../../services/coaching-dtos";
import { canUseCreateSeries } from "../../sessions/sessions-helpers";
import { CohortTabShell } from "./cohort-tab-shell";
import { useCohortSessionsCollection } from "./cohort-sessions-collection";

const services = getCoachingServices();

export function CohortSessionsTab({ cohortId, role }: { cohortId: string; role: CoachingRole }) {
  const query = useHonestQuery<ListResponse<CoachingSessionDto>>(
    coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }),
    () => services.sessions.listForCohort(cohortId)
  );
  const allRows = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const collection = useCohortSessionsCollection(allRows);

  const canCreateSeries = evaluateActionCapability(role, "sessions:create-series").allowed && canUseCreateSeries(role);
  const canClearSeries = evaluateActionCapability(role, "sessions:clear-series").allowed;
  const location = useLocation();
  const cohortPath = `/cohorts/${encodeURIComponent(cohortId)}`;
  const headerActions = canCreateSeries || canClearSeries ? (
    <div className="flex flex-wrap gap-2">
      {canCreateSeries && (
        <Link to={buildRolePath(`${cohortPath}/sessions/series`, role, location.search)} className="no-underline">
          <Button variant="primary" size="sm" data-testid="cohort-create-series">
            Create ten-week schedule
          </Button>
        </Link>
      )}
      {canClearSeries && (
        <Link to={buildRolePath(`${cohortPath}/sessions/clear`, role, location.search)} className="no-underline">
          <Button variant="secondary" size="sm" data-testid="cohort-clear-series">
            Clear future sessions
          </Button>
        </Link>
      )}
    </div>
  ) : undefined;

  return (
    <CohortTabShell
      title="Sessions"
      subtitle={
        canCreateSeries
          ? "Scheduled sessions for this cohort. Create the ten-week schedule once the cohort has participants."
          : "Scheduled sessions for this cohort."
      }
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No sessions scheduled"
      emptyMessage={
        canCreateSeries
          ? "Add participants, then create the ten-week schedule: choose a start date, time and timezone, preview the ten weekly sessions, and create them."
          : "Sessions scheduled for this cohort will appear here."
      }
      headerActions={headerActions}
    >
      <CollectionToolbar
        collection={collection}
        searchPlaceholder="Filter sessions"
        className="rounded-xl border border-line"
        stableChipNames
      />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Cohort sessions"
          emptyTitle="No sessions match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}